import { useState } from 'react';
import { useAppStore } from '@/lib/store';
import type { MeasurementValue, TailorMeasurement } from '@/types/measurements';
import {
  MALE_SHIRT_MEASUREMENTS,
  MALE_TROUSER_MEASUREMENTS,
  FEMALE_BLOUSE_MEASUREMENTS,
  FEMALE_SKIRT_MEASUREMENTS,
  GIRL_DRESS_MEASUREMENTS,
  formatMeasurement,
} from '@/types/measurements';
import { MeasurementRow } from './ui/MeasurementRow';
import { ConfidenceBadge } from './ui/ConfidenceBadge';
import { getConfidenceLevel } from '@/lib/measurement-engine';
import { MeasurementOverlay } from './MeasurementOverlay';

interface GarmentTab {
  id: string;
  label: string;
  measurements: TailorMeasurement[];
}

function getTabsForGender(gender: string): GarmentTab[] {
  if (gender === 'female') {
    return [
      { id: 'blouse', label: 'Blouse', measurements: FEMALE_BLOUSE_MEASUREMENTS },
      { id: 'skirt', label: 'Skirt', measurements: FEMALE_SKIRT_MEASUREMENTS },
      { id: 'dress', label: 'Girl Dress', measurements: GIRL_DRESS_MEASUREMENTS },
    ];
  }
  if (gender === 'male') {
    return [
      { id: 'shirt', label: 'Shirt', measurements: MALE_SHIRT_MEASUREMENTS },
      { id: 'trouser', label: 'Trouser', measurements: MALE_TROUSER_MEASUREMENTS },
    ];
  }
  // 'other' — show all categories
  return [
    { id: 'shirt', label: 'Shirt', measurements: MALE_SHIRT_MEASUREMENTS },
    { id: 'trouser', label: 'Trouser', measurements: MALE_TROUSER_MEASUREMENTS },
    { id: 'blouse', label: 'Blouse', measurements: FEMALE_BLOUSE_MEASUREMENTS },
    { id: 'skirt', label: 'Skirt', measurements: FEMALE_SKIRT_MEASUREMENTS },
    { id: 'dress', label: 'Girl Dress', measurements: GIRL_DRESS_MEASUREMENTS },
  ];
}

export function ResultsScreen() {
  const { measurements, overallConfidence, frontPhoto, frontDetection, sidePhoto, sideDetection, userName, gender, setStep, unitSystem, setUnitSystem } = useAppStore();
  const [showOverlay, setShowOverlay] = useState(false);

  const tabs = getTabsForGender(gender);
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'shirt');

  if (!measurements) {
    return (
      <div className="screen items-center justify-center text-center">
        <p className="text-gray-500">No measurements available.</p>
        <button onClick={() => setStep('welcome')} className="btn-primary mt-4">
          Start Over
        </button>
      </div>
    );
  }

  const currentTab = tabs.find((t) => t.id === activeTab) ?? tabs[0]!;
  const displayMeasurements = currentTab.measurements
    .filter((m) => {
      const value = measurements[m.key];
      return value && value.valueCm > 0;
    })
    .map((m) => ({
      key: m.key,
      label: m.tailorLabel,
      value: measurements[m.key] as MeasurementValue,
    }));

  function handleShareWhatsApp() {
    const lines = [
      userName ? `*${userName}'s FitAfrica Measurements*` : '*My FitAfrica Measurements*',
      '',
    ];

    // Include all tabs in the share
    for (const tab of tabs) {
      const tabMeasurements = tab.measurements
        .filter((m) => {
          const value = measurements![m.key];
          return value && value.valueCm > 0;
        })
        .map((m) => ({
          label: m.tailorLabel,
          value: measurements![m.key] as MeasurementValue,
        }));

      if (tabMeasurements.length > 0) {
        lines.push(`*${tab.label}*`);
        for (const m of tabMeasurements) {
          lines.push(`${m.label}: ${formatMeasurement(m.value.valueCm, unitSystem)}`);
        }
        lines.push('');
      }
    }

    lines.push(
      `Overall confidence: ${Math.round(overallConfidence * 100)}%`,
      '',
      'Measured with FitAfrica - AI Body Measurements'
    );

    const text = encodeURIComponent(lines.join('\n'));
    window.open(`https://wa.me/?text=${text}`, '_blank');
  }

  return (
    <div className="screen">
      <div className="flex-1">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {userName ? `${userName}'s Measurements` : 'Your Measurements'}
          </h2>
          <div className="mt-2 flex items-center justify-center gap-2">
            <span className="text-sm text-gray-500">Overall accuracy:</span>
            <ConfidenceBadge level={getConfidenceLevel(overallConfidence)} />
          </div>
        </div>

        {/* Unit toggle */}
        <div className="mt-3 flex justify-center">
          <div className="flex rounded-lg bg-gray-100 p-0.5">
            <button
              onClick={() => setUnitSystem('cm')}
              className={`rounded-md px-4 py-1.5 text-xs font-medium transition-colors ${
                unitSystem === 'cm' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              cm
            </button>
            <button
              onClick={() => setUnitSystem('inches')}
              className={`rounded-md px-4 py-1.5 text-xs font-medium transition-colors ${
                unitSystem === 'inches' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              in
            </button>
          </div>
        </div>

        {/* Garment category tabs */}
        <div className="mt-4 flex gap-1 overflow-x-auto rounded-xl bg-gray-100 p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-brand-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Measurements list */}
        <div className="card mt-4">
          {displayMeasurements.length > 0 ? (
            displayMeasurements.map((m) => (
              <MeasurementRow key={`${activeTab}-${m.key}`} label={m.label} value={m.value} />
            ))
          ) : (
            <p className="py-4 text-center text-sm text-gray-400">
              No measurements available for this category.
            </p>
          )}
        </div>

        {/* Tip */}
        <p className="mt-3 text-center text-xs text-gray-400">
          {displayMeasurements.length} measurements shown for {currentTab.label}.
          Tap tabs above to switch garment type.
        </p>

        {/* View on Body button */}
        {frontDetection && (
          <button
            onClick={() => setShowOverlay(true)}
            className="mt-4 w-full rounded-xl border-2 border-brand-200 bg-brand-50 px-4 py-3 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100"
          >
            View on Body
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="mt-6 space-y-3">
        <button onClick={handleShareWhatsApp} className="btn-primary">
          Share on WhatsApp
        </button>
        <button
          onClick={() => setStep('capture_front')}
          className="btn-secondary"
        >
          Retake Photo
        </button>
        <button
          onClick={() => setStep('welcome')}
          className="w-full py-3 text-sm text-gray-500"
        >
          Start Over
        </button>
      </div>

      {/* Measurement overlay modal */}
      {showOverlay && frontDetection && measurements && (
        <MeasurementOverlay
          photo={frontPhoto}
          detection={frontDetection}
          measurements={measurements}
          onClose={() => setShowOverlay(false)}
          sidePhoto={sidePhoto}
          sideDetection={sideDetection}
        />
      )}
    </div>
  );
}
